import { Group } from '@mantine/core';
import { useIntl } from 'react-intl';

import { CustomNumberInput } from './custom-number-input';
import styles from './dimensions-input.module.css';
import { inputMessages } from './input-messages';
import { createNumberInputBlurHandler } from '../../utils/number-input';

import type { Dimensions } from '../../types/dimensions';
import type { MantineSize } from '@mantine/core';

export type DimensionsInputProps = {
  value: Dimensions;
  /** Fires per keystroke with the updated Dimensions object. */
  onChange: (next: Dimensions) => void;
  /** Fires on blur with the final cleaned numeric. */
  onCommit?: (next: Dimensions) => void;
  disabled?: boolean;
  size?: MantineSize;
};

export const DimensionsInput = ({ value, onChange, onCommit, disabled = false, size }: DimensionsInputProps) => {
  const intl = useIntl();
  const { unit } = value;

  const emitChange = (field: 'length' | 'width' | 'height', fieldValue: number) => {
    onChange({ ...value, unit, [field]: fieldValue });
  };

  const emitCommit = (field: 'length' | 'width' | 'height', fieldValue: number) => {
    const next: Dimensions = { ...value, unit, [field]: fieldValue };
    onChange(next);
    if (onCommit) onCommit(next);
  };

  const handleLengthChange = (next: number | string) => {
    const numValue = typeof next === 'string' ? parseFloat(next) : next;
    if (isNaN(numValue)) return;
    emitChange('length', numValue);
  };

  const handleWidthChange = (next: number | string) => {
    const numValue = typeof next === 'string' ? parseFloat(next) : next;
    if (isNaN(numValue)) return;
    emitChange('width', numValue);
  };

  const handleHeightChange = (next: number | string) => {
    const numValue = typeof next === 'string' ? parseFloat(next) : next;
    if (isNaN(numValue)) return;
    emitChange('height', numValue);
  };

  const handleLengthBlur = createNumberInputBlurHandler((cleaned) => emitCommit('length', cleaned));
  const handleWidthBlur = createNumberInputBlurHandler((cleaned) => emitCommit('width', cleaned));
  const handleHeightBlur = createNumberInputBlurHandler((cleaned) => emitCommit('height', cleaned));

  return (
    <Group align='flex-end' gap='sm'>
      <CustomNumberInput
        allowNegative={false}
        clampBehavior='strict'
        className={styles.inputFlex}
        data-testid='package-length-input'
        decimalScale={2}
        defaultValue={0}
        disabled={disabled}
        min={0}
        onBlur={handleLengthBlur}
        onChange={handleLengthChange}
        placeholder={intl.formatMessage(inputMessages.zeroPlaceholder)}
        rightSectionWidth={70}
        size={size}
        step={1}
        internalLabel={intl.formatMessage(inputMessages.lengthLabel)}
        value={value.length ?? 0}
      />
      <CustomNumberInput
        allowNegative={false}
        clampBehavior='strict'
        className={styles.inputFlex}
        data-testid='package-width-input'
        decimalScale={3}
        defaultValue={0}
        disabled={disabled}
        min={0}
        onBlur={handleWidthBlur}
        onChange={handleWidthChange}
        placeholder={intl.formatMessage(inputMessages.zeroPlaceholder)}
        rightSectionWidth={70}
        size={size}
        step={1}
        internalLabel={intl.formatMessage(inputMessages.widthLabel)}
        value={value.width ?? 0}
      />
      <CustomNumberInput
        allowNegative={false}
        clampBehavior='strict'
        className={styles.inputFlex}
        data-testid='package-height-input'
        decimalScale={3}
        defaultValue={0}
        disabled={disabled}
        min={0}
        onBlur={handleHeightBlur}
        onChange={handleHeightChange}
        placeholder={intl.formatMessage(inputMessages.zeroPlaceholder)}
        rightSectionWidth={70}
        size={size}
        step={1}
        internalLabel={intl.formatMessage(inputMessages.heightLabel)}
        value={value.height ?? 0}
      />
    </Group>
  );
};
